import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import PlaceCashBanner from '@tcp/core/src/components/features/CnC/PlaceCashBanner';
import { getDynamicLabelValues } from '@tcp/core/src/utils/labels/labelUtilty.js';
import { isMobileWeb } from '@tcp/core/src/utils';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import withStyles from '../../../../../../common/hoc/withStyles';
import Row from '../../../../../../common/atoms/Row';
import Col from '../../../../../../common/atoms/Col';
import OrderLedgerContainer from '../../OrderLedger';
import CouponAndPromos from '../../CouponAndPromos';
import BonusPointsDays from '../../../../../../common/organisms/BonusPointsDays';
import SigninCtaBanner from '../../../molecules/SigninCtaBanner';

/** The hard coded values are just to show the confirmation template. these will be removed once the components are are in place */
import styles from '../styles/CnCTemplate.style';
import PersonalizedCoupons from '../../../../Confirmation/organisms/PersonalizedCoupons';
import BAG_CONSTANTS from '../../../../BagPage/BagPage.constants';
import FreeShippingProgressBar from '../../FreeShippingProgressBar';

const Recommendations = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "recommendations" */ '@tcp/core/src/components/common/molecules/Recommendations'
  )
);

const getBonusPointsDaysSection = ({ isGuest, showAccordian, newBag }) => {
  return (
    !isGuest && (
      <BonusPointsDays
        showAccordian={showAccordian}
        enableApplyCta
        additionalClassNameModal="bonus-modal-web"
        newBag={newBag}
      />
    )
  );
};

const getRecommendationSection = (isConfirmationPage, excludedIds, partNumber) => {
  const headerLabelCheckout = getDynamicLabelValues('checkout.bagPage.lbl_confirmation_page');
  const headerLabelSimilarStyles = getDynamicLabelValues(
    'dynamic.cart.lbl_confirmation_page_checkout_similar_style'
  );
  return (
    isConfirmationPage && (
      <div className="recommendationsWrapper">
        <Recommendations
          page={Constants.RECOMMENDATIONS_PAGES_MAPPING.CHECKOUT}
          variations="moduleO"
          excludedIds={excludedIds}
          partNumber={partNumber}
          headerLabel={headerLabelCheckout}
          sequence="1"
        />
        <Recommendations
          page={Constants.RECOMMENDATIONS_PAGES_MAPPING.BAG}
          portalValue={Constants.RECOMMENDATIONS_MBOXNAMES.CHECKOUT_SIMILAR_STYLES}
          variations="moduleO"
          excludedIds={excludedIds}
          partNumber={partNumber}
          headerLabel={headerLabelSimilarStyles}
          sequence="2"
        />
      </div>
    )
  );
};

const getCouponsAndPromos = (
  isCheckoutView,
  orderLedgerAfterView,
  showAccordian,
  newBag,
  spcBag,
  isCouponSmoothScrollEnabled
) => (
  <CouponAndPromos
    fullPageInfo={!isCheckoutView || orderLedgerAfterView}
    showAccordian={showAccordian}
    additionalClassNameModal="coupon-modal-web"
    idPrefix="desktop"
    isCheckoutFlow={isCheckoutView}
    newBag={newBag}
    spcBag={spcBag}
    variation={'smooth-carousel'}
    fromBagPage
    isCouponSmoothScrollEnabled={isCouponSmoothScrollEnabled}
  />
);

const getFreeShippingProgressBar = ({
  shipments,
  freeShippingThreshold,
  productTotal,
  dynamicShippingCharge,
  isBossEnabled,
  shippingMethodSelected,
  isMixedOrder,
  isFreeShippingProgressBarDisabled,
  isNoNEmptyBag,
  isShippingOrder,
  isFreeShippingCouponApplied,
  freeShippingPromoType,
  freeShippingMessageForPromosEnabled,
  isMobileWebTileEnhancementEnabled,
}) => {
  const shipmentsObject = shipments && JSON.parse(JSON.stringify(shipments));
  const shippingDetails = shipmentsObject?.filter(
    (shipment) => shipment?.c_fromStoreId === null
  )[0];

  const shippingMethodCodeSelected =
    shippingMethodSelected?.code || shippingDetails?.shippingMethod?.c_code;
  const shippingFreePercentage = (shippingDetails?.productTotal / freeShippingThreshold) * 100;
  const isShippingMethodApplicableForFreeShipping = BAG_CONSTANTS.FREE_SHIPMENT_CODES?.includes(
    shippingMethodCodeSelected
  );
  const showFreeShippingProgressBar =
    isNoNEmptyBag &&
    isShippingMethodApplicableForFreeShipping &&
    isShippingOrder &&
    !isFreeShippingProgressBarDisabled;

  const showProgressBarComp = freeShippingMessageForPromosEnabled || freeShippingThreshold > 0;
  return (
    showFreeShippingProgressBar &&
    showProgressBarComp && (
      <FreeShippingProgressBar
        shipments={shipmentsObject}
        freeShippingThreshold={freeShippingThreshold}
        productTotal={productTotal}
        dynamicShippingCharge={dynamicShippingCharge}
        isBossEnabled={isBossEnabled}
        shippingFreePercentage={shippingFreePercentage}
        isMixedOrder={isMixedOrder}
        isFreeShippingCouponApplied={isFreeShippingCouponApplied}
        freeShippingPromoType={freeShippingPromoType}
        freeShippingMessageForPromosEnabled={freeShippingMessageForPromosEnabled}
        isMobileWebTileEnhancementEnabled={isMobileWebTileEnhancementEnabled}
      />
    )
  );
};

const getSFL = (wrapSection, saveLaterSection, orderItemsCount) => {
  return (
    <div className="rounded-container new-sfl-section">
      {wrapSection(saveLaterSection(), orderItemsCount)}
    </div>
  );
};

const showSigninCta = (showBannerMobileWeb, isNoNEmptyBag, rememberedUser, isGuest) => {
  return showBannerMobileWeb && isNoNEmptyBag && !rememberedUser && isGuest;
};

const showConfirmationSignin = (showBannerDesktop, isNoNEmptyBag, rememberedUser, isGuest) => {
  return showBannerDesktop && isNoNEmptyBag && !rememberedUser && isGuest;
};

const getBannerDesktop = (isSignInBannerAbTestEnabled, isBagPage) => {
  return isSignInBannerAbTestEnabled && isBagPage && !isMobileWeb();
};

const getBannerMobile = (isSignInBannerAbTestEnabled, isBagPage) => {
  return isSignInBannerAbTestEnabled && isBagPage && isMobileWeb();
};

// eslint-disable-next-line complexity
const CnCTemplate = ({
  leftSection: LeftSection,
  showLeftSection,
  className,
  header: Header,
  isGuest,
  isCheckoutView,
  showAccordian,
  isConfirmationPage,
  orderLedgerAfterView,
  pageCategory,
  excludedIds,
  partNumber,
  isSignInBannerAbTestEnabled,
  isBagPage,
  isNoNEmptyBag,
  rememberedUser,
  newBag,
  isStickyCheckoutButtonEnabled,
  spcBag,
  getEstimatedTaxAction,
  getIsFromEstimatedTaxForm,
  getIsTotalCalculatedAction,
  isTotalCalculated,
  isNewCouponPositionEnabled,
  saveLaterSection,
  wrapSection,
  orderItemsCount,
  isCouponSmoothScrollEnabled,
  renderCondForPLCCTopBanner,
  renderEstimatedTotal: EstimatedTotal,
  shipments,
  productTotal,
  freeShippingThreshold,
  dynamicShippingCharge,
  isBossEnabled,
  shippingMethodSelected,
  isMixedOrder,
  isFreeShippingProgressBarDisabled,
  isShippingOrder,
  isFreeShippingCouponApplied,
  freeShippingPromoType,
  freeShippingMessageForPromosEnabled,
  isMobileWebTileEnhancementEnabled,
}) => {
  const hasMounted = useHasMounted();
  const isSmallLeftSection = showLeftSection;
  const [showBannerDesktop, setIsShowBannerDesktop] = useState(false);
  const [showBannerMobileWeb, setIsShowBannerMobileWeb] = useState(false);

  useEffect(() => {
    const bannerDesktop = getBannerDesktop(isSignInBannerAbTestEnabled, isBagPage);
    const bannerMobileWeb = getBannerMobile(isSignInBannerAbTestEnabled, isBagPage);
    setIsShowBannerDesktop(bannerDesktop);
    setIsShowBannerMobileWeb(bannerMobileWeb);
  }, [isSignInBannerAbTestEnabled]);
  return (
    <section className={`${className} bag-items-section`}>
      {Header && <Header />}
      <Row className="bag-items">
        <Col
          colSize={{
            small: 6,
            medium: isSmallLeftSection ? 5 : 8,
            large: isSmallLeftSection ? 8 : 12,
          }}
          className="left-sec "
        >
          {EstimatedTotal && <EstimatedTotal />}
          {showSigninCta(showBannerMobileWeb, isNoNEmptyBag, rememberedUser, isGuest) && (
            <SigninCtaBanner />
          )}
          <LeftSection />
        </Col>
        {showLeftSection && (
          <Col
            colSize={{ small: 6, medium: 3, large: 4 }}
            className={`right-sec ${isCheckoutView ? 'hide-mobile' : ''}`}
          >
            {isNewCouponPositionEnabled && (
              <div className={!isMobileWeb() && 'elem-mt-MED_1'}>
                {getCouponsAndPromos(
                  isCheckoutView,
                  orderLedgerAfterView,
                  showAccordian,
                  newBag,
                  spcBag,
                  isCouponSmoothScrollEnabled
                )}
              </div>
            )}
            <>
              {isConfirmationPage ? (
                <>
                  <OrderLedgerContainer
                    isConfirmationPage={isConfirmationPage}
                    pageCategory="confirmation"
                    wrapSection={wrapSection}
                    orderItemsCount={orderItemsCount}
                  />
                  {hasMounted &&
                    isMobileWeb() &&
                    getSFL(wrapSection, saveLaterSection, orderItemsCount)}
                  <Row fullBleed>
                    <Col colSize={{ small: 6, medium: 8, large: 12 }}>
                      <PersonalizedCoupons />
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {showConfirmationSignin(
                    showBannerDesktop,
                    isNoNEmptyBag,
                    rememberedUser,
                    isGuest
                  ) && <SigninCtaBanner />}
                  {getFreeShippingProgressBar({
                    shipments,
                    freeShippingThreshold,
                    productTotal,
                    dynamicShippingCharge,
                    isBossEnabled,
                    shippingMethodSelected,
                    isMixedOrder,
                    isFreeShippingProgressBarDisabled,
                    isNoNEmptyBag,
                    isShippingOrder,
                    isFreeShippingCouponApplied,
                    freeShippingPromoType,
                    freeShippingMessageForPromosEnabled,
                    isMobileWebTileEnhancementEnabled,
                  })}
                  <OrderLedgerContainer
                    orderLedgerAfterView={orderLedgerAfterView}
                    pageCategory={pageCategory}
                    newBag={newBag}
                    isStickyCheckoutButtonEnabled={isStickyCheckoutButtonEnabled}
                    spcBag={spcBag}
                    getEstimatedTaxAction={getEstimatedTaxAction}
                    getIsTotalCalculatedAction={getIsTotalCalculatedAction}
                    getIsFromEstimatedTaxForm={getIsFromEstimatedTaxForm}
                    isTotalCalculated={isTotalCalculated}
                    wrapSection={wrapSection}
                    orderItemsCount={orderItemsCount}
                    renderCondForPLCCTopBanner={renderCondForPLCCTopBanner}
                  />
                  {getBonusPointsDaysSection({ isGuest, showAccordian, newBag })}
                  {pageCategory === BAG_CONSTANTS.BAG_PAGE && (
                    <PlaceCashBanner newBag={newBag} spcBag={spcBag} />
                  )}
                  {!isNewCouponPositionEnabled &&
                    getCouponsAndPromos(
                      isCheckoutView,
                      orderLedgerAfterView,
                      showAccordian,
                      newBag,
                      spcBag
                    )}
                  {hasMounted &&
                    isMobileWeb() &&
                    getSFL(wrapSection, saveLaterSection, orderItemsCount)}
                </>
              )}
            </>
          </Col>
        )}
      </Row>
      {getRecommendationSection(isConfirmationPage, excludedIds, partNumber)}
    </section>
  );
};

CnCTemplate.propTypes = {
  className: PropTypes.string.isRequired,
  header: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  orderLedgerAfterView: PropTypes.shape({}).isRequired,
  leftSection: PropTypes.node.isRequired,
  showLeftSection: PropTypes.bool,
  isGuest: PropTypes.bool.isRequired,
  showAccordian: PropTypes.bool,
  isCheckoutView: PropTypes.bool,
  isConfirmationPage: PropTypes.bool,
  pageCategory: PropTypes.string,
  excludedIds: PropTypes.string,
  partNumber: PropTypes.string,
  isSignInBannerAbTestEnabled: PropTypes.bool.isRequired,
  isBagPage: PropTypes.bool.isRequired,
  isNoNEmptyBag: PropTypes.bool.isRequired,
  rememberedUser: PropTypes.bool.isRequired,
  newBag: PropTypes.bool,
  isStickyCheckoutButtonEnabled: PropTypes.bool,
  spcBag: PropTypes.bool,
  renderEstimatedTotal: PropTypes.func,
};

CnCTemplate.defaultProps = {
  header: false,
  showLeftSection: true,
  showAccordian: true,
  isCheckoutView: false,
  isConfirmationPage: false,
  pageCategory: '',
  excludedIds: '',
  partNumber: '',
  newBag: false,
  spcBag: false,
  renderEstimatedTotal: () => {},
};

export default withStyles(CnCTemplate, styles);
export { CnCTemplate as CnCTemplateVanilla };
